
export interface MultiLingualContent {
  text: string;
  language: 'en' | 'fr';
}

export interface License {
  id: string;
  name: string;
  description: string;
  rights_holder: string;
  mentions: string[];
  hashtags: string[];
  permissions: MultiLingualContent[];
  restrictions: MultiLingualContent[];
  clip_language: string;
  license_validity_date?: string;
  geographic_zone: string;
  authorized_platforms: string[];
  max_extract_duration?: number;
  native_monetization_allowed: boolean;
  ai_editing_tools_allowed: boolean;
  ai_visual_transformation_allowed: boolean;
  ai_training_database_allowed: boolean;
  challenge_participation: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LicenseVariables {
  rightsholder: string;
  mentions: string[];
  hashtags: string[];
  clip_language: string;
  license_validity_date?: string;
  geographic_zone: string;
  authorized_platforms: string[];
  max_extract_duration?: number;
  native_monetization_allowed: boolean;
  ai_editing_tools_allowed: boolean;
  ai_visual_transformation_allowed: boolean;
  ai_training_database_allowed: boolean;
  challenge_participation: boolean;
}

export interface BasicLicenseVariables {
  rightsholder: string;
  mentions: string[];
  hashtags: string[];
}

/**
 * Transform license variables for database storage
 */
export function transformLicenseVariablesForStorage(variables: LicenseVariables) {
  return {
    rights_holder: variables.rightsholder,
    mentions: variables.mentions,
    hashtags: variables.hashtags,
    clip_language: variables.clip_language,
    license_validity_date: variables.license_validity_date,
    geographic_zone: variables.geographic_zone,
    authorized_platforms: variables.authorized_platforms,
    max_extract_duration: variables.max_extract_duration,
    native_monetization_allowed: variables.native_monetization_allowed,
    ai_editing_tools_allowed: variables.ai_editing_tools_allowed,
    ai_visual_transformation_allowed: variables.ai_visual_transformation_allowed,
    ai_training_database_allowed: variables.ai_training_database_allowed,
    challenge_participation: variables.challenge_participation
  };
}

/**
 * Maps a database license object to our License type
 */
export function mapDatabaseLicenseToLicense(dbLicense: any): License {
  // Parse permissions from database format
  let permissions: MultiLingualContent[] = [];
  if (dbLicense.permissions) {
    try {
      // If it's a string array with our format "text|language"
      if (Array.isArray(dbLicense.permissions) && dbLicense.permissions.length > 0) {
        if (typeof dbLicense.permissions[0] === 'string') {
          if (dbLicense.permissions[0].includes('|')) {
            // Format "text|language"
            permissions = dbLicense.permissions.map((item: string) => {
              const [text, language] = item.split('|');
              return { text, language: language as 'en' | 'fr' };
            });
          } else {
            // Legacy format: convert simple strings to MultiLingualContent
            permissions = dbLicense.permissions.map((text: string) => ({ 
              text, 
              language: 'en' 
            }));
          }
        }
        // If already in MultiLingualContent format
        else if (typeof dbLicense.permissions[0] === 'object') {
          permissions = dbLicense.permissions;
        }
      }
      // If it's a JSON string, parse it
      else if (typeof dbLicense.permissions === 'string' && dbLicense.permissions.startsWith('[{')) {
        permissions = JSON.parse(dbLicense.permissions);
      }
    } catch (e) {
      console.error("Error parsing permissions:", e);
      // If parsing fails, leave as empty array
    }
  }

  // Parse restrictions from database format
  let restrictions: MultiLingualContent[] = [];
  if (dbLicense.restrictions) {
    try {
      // If it's a string array with our format "text|language"
      if (Array.isArray(dbLicense.restrictions) && dbLicense.restrictions.length > 0) {
        if (typeof dbLicense.restrictions[0] === 'string') {
          if (dbLicense.restrictions[0].includes('|')) {
            // Format "text|language"
            restrictions = dbLicense.restrictions.map((item: string) => {
              const [text, language] = item.split('|');
              return { text, language: language as 'en' | 'fr' };
            });
          } else {
            // Legacy format: convert simple strings to MultiLingualContent
            restrictions = dbLicense.restrictions.map((text: string) => ({ 
              text, 
              language: 'en' 
            }));
          }
        }
        // If already in MultiLingualContent format
        else if (typeof dbLicense.restrictions[0] === 'object') {
          restrictions = dbLicense.restrictions;
        }
      }
      // If it's a JSON string, parse it
      else if (typeof dbLicense.restrictions === 'string' && dbLicense.restrictions.startsWith('[{')) {
        restrictions = JSON.parse(dbLicense.restrictions);
      }
    } catch (e) {
      console.error("Error parsing restrictions:", e);
      // If parsing fails, leave as empty array
    }
  }

  return {
    id: dbLicense.id || "",
    name: dbLicense.name || "",
    description: dbLicense.description || "",
    rights_holder: dbLicense.rights_holder || "",
    mentions: dbLicense.mentions || [],
    hashtags: dbLicense.hashtags || [],
    permissions,
    restrictions,
    clip_language: dbLicense.clip_language || 'fr',
    license_validity_date: dbLicense.license_validity_date,
    geographic_zone: dbLicense.geographic_zone || 'Monde entier',
    authorized_platforms: dbLicense.authorized_platforms || ['TikTok', 'Instagram', 'Facebook', 'Youtube'],
    max_extract_duration: dbLicense.max_extract_duration,
    native_monetization_allowed: dbLicense.native_monetization_allowed || false,
    ai_editing_tools_allowed: dbLicense.ai_editing_tools_allowed !== false,
    ai_visual_transformation_allowed: dbLicense.ai_visual_transformation_allowed || false,
    ai_training_database_allowed: dbLicense.ai_training_database_allowed || false,
    challenge_participation: dbLicense.challenge_participation !== false,
    createdAt: dbLicense.createdAt || new Date().toISOString(),
    updatedAt: dbLicense.updatedAt || new Date().toISOString()
  };
}

/**
 * Get localized content from MultiLingualContent array
 */
export function getLocalizedContent(content: MultiLingualContent[], language: 'en' | 'fr'): string[] {
  if (!Array.isArray(content)) return [];
  
  return content
    .filter(item => item.language === language)
    .map(item => item.text);
}

/**
 * Create default license variables with safe defaults
 */
export function createDefaultLicenseVariables(partial: Partial<LicenseVariables> | BasicLicenseVariables | License = {}): LicenseVariables {
  // Handle different input types safely
  const getProperty = (key: string, fallback: any) => {
    if (key === 'rightsholder') {
      return (partial as any).rightsholder || (partial as any).rights_holder || fallback;
    }
    // For boolean values, only use the fallback if the property is explicitly undefined
    if (typeof fallback === 'boolean') {
      const value = (partial as any)[key];
      return value !== undefined ? Boolean(value) : fallback;
    }
    return (partial as any)[key] || fallback;
  };

  return {
    rightsholder: getProperty('rightsholder', ""),
    mentions: getProperty('mentions', []),
    hashtags: getProperty('hashtags', []),
    clip_language: getProperty('clip_language', 'fr'),
    license_validity_date: getProperty('license_validity_date', undefined),
    geographic_zone: getProperty('geographic_zone', 'Monde entier'),
    authorized_platforms: getProperty('authorized_platforms', ['TikTok', 'Instagram', 'Facebook', 'Youtube']),
    max_extract_duration: getProperty('max_extract_duration', undefined),
    native_monetization_allowed: getProperty('native_monetization_allowed', false),
    ai_editing_tools_allowed: getProperty('ai_editing_tools_allowed', true),
    ai_visual_transformation_allowed: getProperty('ai_visual_transformation_allowed', false),
    ai_training_database_allowed: getProperty('ai_training_database_allowed', false),
    challenge_participation: getProperty('challenge_participation', true)
  };
}

/**
 * Create basic license variables from simple parameters
 */
export function createBasicLicenseVariables(basic: BasicLicenseVariables): LicenseVariables {
  return createDefaultLicenseVariables(basic);
}
